import React from "react";
import styled from "styled-components";
import useGridCardStyles from "./styles";

export interface GridCardItemProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  badge?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

export interface GridCardProps<T = any> {
  data?: T[];
  renderCard: (item: T, index: number) => GridCardItemProps;
  emptyMessage?: React.ReactNode;
  className?: string;
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  totalRecords?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageLengthMenu?: number[];
}
const GridContainer = styled.div<{ $styles: any }>`
  ${(props) => props.$styles.container}
`;

const ItemWrapper = styled.div<{ $styles: any; $isClickable: boolean }>`
  ${(props) => props.$styles.item}
  cursor: ${(props) => (props.$isClickable ? "pointer" : "default")};
`;

const GridCardItemComponent: React.FC<GridCardItemProps> = ({
  title,
  subtitle,
  badge,
  children,
  footer,
  onClick,
  className,
}) => {
  const styles = useGridCardStyles();
  return (
    <ItemWrapper
      $styles={styles}
      $isClickable={!!onClick}
      onClick={onClick}
      className={className}
    >
      <div style={styles.header}>
        <div>
          <div style={styles.title}>{title}</div>
          {subtitle && <div style={styles.subtitle}>{subtitle}</div>}
        </div>
        {badge && <div>{badge}</div>}
      </div>

      {children && <div style={styles.body}>{children}</div>}

      {footer && <div style={styles.footer}>{footer}</div>}
    </ItemWrapper>
  );
};


interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageLengthMenu?: number[];
}

const GridCardPaginationComponent: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  totalRecords,
  onPageChange,
  onPageSizeChange,
  pageLengthMenu = [9, 18, 27, 45, 90],
}) => {
  const styles = useGridCardStyles();

  if (totalRecords === 0) return null;

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalRecords);

  return (
    <div style={styles.pagination}>
      <div style={styles.pageSizeSelector}>
        {onPageSizeChange && (
          <>
            <select
              style={styles.pageSizeSelect}
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {pageLengthMenu.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            Rows per page
          </>
        )}
      </div>

      <div style={styles.paginationInfo}>
        {start} - {end} of {totalRecords}
      </div>

      <div style={styles.paginationControls}>
        <button
          style={{
            ...styles.paginationButton,
            ...(currentPage === 1 ? styles.disabledButton : {}),
          }}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &laquo;
        </button>

        {[...Array(totalPages)].map((_, i) => {
          const page = i + 1;
          const isActive = currentPage === page;

          if (
            totalPages > 7 &&
            page !== 1 &&
            page !== totalPages &&
            Math.abs(page - currentPage) > 2
          ) {
            if (Math.abs(page - currentPage) === 3) {
              return (
                <span
                  key={`ellipsis-${page}`}
                  style={{
                    ...styles.paginationButton,
                    cursor: "default",
                  }}
                >
                  ...
                </span>
              );
            }
            return null;
          }

          return (
            <button
              key={page}
              style={{
                ...styles.paginationButton,
                ...(isActive ? styles.activePage : {}),
              }}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          );
        })}

        <button
          style={{
            ...styles.paginationButton,
            ...(currentPage === totalPages || totalPages === 0
              ? styles.disabledButton
              : {}),
          }}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          &raquo;
        </button>
      </div>
    </div>
  );
};

function GridCard<T = any>({
  data = [],
  renderCard,
  emptyMessage = "No records found.",
  className,
  currentPage,
  totalPages,
  pageSize,
  totalRecords,
  onPageChange,
  onPageSizeChange,
  pageLengthMenu,
}: GridCardProps<T>) {
  const styles = useGridCardStyles();

  const showPagination =
    currentPage !== undefined &&
    totalPages !== undefined &&
    pageSize !== undefined &&
    totalRecords !== undefined &&
    onPageChange !== undefined;

  return (
    <GridContainer $styles={styles} className={className}>
      {data.length === 0 ? (
        <div style={styles.emptyState}>{emptyMessage}</div>
      ) : (
        data.map((item, index) => {
          const cardProps = renderCard(item, index);
          return <GridCardItemComponent key={index} {...cardProps} />;
        })
      )}

      {showPagination && (
        <GridCardPaginationComponent
          currentPage={currentPage!}
          totalPages={totalPages!}
          pageSize={pageSize!}
          totalRecords={totalRecords!}
          onPageChange={onPageChange!}
          onPageSizeChange={onPageSizeChange}
          pageLengthMenu={pageLengthMenu}
        />
      )}
    </GridContainer>
  );
}

export { GridCard };
